import React, { useMemo, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Tooltip } from '@mui/material';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { emptyString } from 'constants/emptyConst';
import { SELECT } from 'constants/inputTypes';

const AssetOrderComponent = ({
  t,
  rights,
  onPropertyChange,
  properties,
  assetCategories,
  assetClassifications,
  processing,
  isFacilityFrame,
  validationInfo,
  addToRefs,
  onSearch
}) => {
  const { orderNumber, category, classification } = properties;
  const readOnly = useMemo(() => rights.readOnlyFull || !rights.financedAssetWrite, [rights]);
  const onChange = useCallback(key => onPropertyChange(key), [onPropertyChange]);
  const onCategoryChange = useCallback(({ value }) => onPropertyChange('category', value), [onPropertyChange]);
  const onClassificationChange = useCallback(({ value }) => onPropertyChange('classification', value), [onPropertyChange]);

  return (
    <Grid container spacing={3}>
      {!isFacilityFrame && (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offer-asset-card_order-number'}
            fullWidth
            value={orderNumber || emptyString}
            onChange={onChange('orderNumber')}
            label={t('OfferEdit.Asset.Info.OrderNumber')}
            disabled={readOnly || processing}
            endAdornment={
              rights?.assetSearchByCommandNumber ? (
                <Tooltip title={t('OfferEdit.Asset.SearchCommandNumber')}>
                  <DriveEtaIcon id='search_orderNumber' onClick={() => onSearch?.(orderNumber)} style={{ cursor: 'pointer' }} />
                </Tooltip>
              ) : null
            }
          />
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sm={6}
        md={4}
        id={t('OfferEdit.Asset.Info.Category')}
        ref={ref => addToRefs(ref, t('OfferEdit.Asset.Info.Category'), SELECT)}
      >
        <Autocomplete
          id={'offer-asset-card_category'}
          label={t('OfferEdit.Asset.Info.Category')}
          fullWidth
          required
          error={Boolean(validationInfo)}
          helperText={validationInfo}
          value={category || emptyString}
          onChange={value => onCategoryChange(value ? value : {})}
          valueKey='value'
          labelKey='value'
          options={assetCategories}
          disabled={readOnly || processing}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          id={'offer-asset-card_classification'}
          label={t('OfferEdit.Asset.Info.Classification')}
          fullWidth
          value={classification || emptyString}
          onChange={value => onClassificationChange(value ? value : {})}
          valueKey='value'
          labelKey='value'
          options={assetClassifications}
          disabled={readOnly || processing}
        />
      </Grid>
    </Grid>
  );
};

AssetOrderComponent.displayName = 'AssetOrderComponent';

AssetOrderComponent.propTypes = {
  t: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  properties: PropTypes.object.isRequired,
  assetCategories: PropTypes.array.isRequired,
  assetClassifications: PropTypes.array.isRequired,
  processing: PropTypes.bool,
  isFacilityFrame: PropTypes.bool,
  validationInfo: PropTypes.string,
  addToRefs: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired
};

const MemoizedAssetOrderComponent = withTranslation()(withRights(AssetOrderComponent));

const ForwardedAssetOrderComponent = forwardRef((props, ref) => <MemoizedAssetOrderComponent {...props} forwardedRef={ref} />);

ForwardedAssetOrderComponent.displayName = 'ForwardedAssetOrderComponent';

export default ForwardedAssetOrderComponent;
